import { DateTime } from '@ksyos/date-time';
import { useState } from 'react';
import type { IAddendumAddEditValue } from '@frontend/components/elements/AddendumAddEdit';
import type { IDiagnosticReportGetOne } from '@frontend/components/interfaces';
import { ProcessEntity } from '@common/enums/processEntity';
import { ServiceCode } from '@common/enums/serviceCode';
import { addAddendumTimeCheck } from '@common/utils/addAddendumTimeCheck';
import { AddendumAddEdit, AddendumAddEditValueInputType } from '@frontend/components/elements/AddendumAddEdit';
import { createAddendum } from '@frontend/services/api/addendumApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { useInterval } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import {
    conclusionSelectOptions,
    getAdviceOptions,
    relationEcgAndComplaintsOptions,
} from './TcohV2DiagnosticReportAddEditCustomControlFields';

interface ITcohV2DiagnosticReportAddendumAddProps {
    diagnosticReport: IDiagnosticReportGetOne;
    onCancel: () => void;
    onSaved: () => void;
}

export function TcohV2DiagnosticReportAddendumAdd(props: Readonly<ITcohV2DiagnosticReportAddendumAddProps>) {
    const abortSignal = useAbortSignal();
    const translate = getSelectedLanguage();
    const { diagnosticReport, onCancel, onSaved } = props;
    const { customControlData, id } = diagnosticReport;

    const [disableInput, setDisableInput] = useState(false);
    const [disableSend, setDisableSend] = useState(true);

    const [addendum, setAddendum] = useState<IAddendumAddEditValue[]>([
        {
            key: 'conclusie',
            value: {
                label: translate.common.conclusion,
                options: conclusionSelectOptions(),
                type: AddendumAddEditValueInputType.select,
                value: customControlData?.conclusie,
            },
        },
        {
            key: 'advies',
            value: {
                label: translate.diagnosticReport.advice,
                options: getAdviceOptions(customControlData?.advies),
                type: AddendumAddEditValueInputType.typeAhead,
                value: customControlData?.advies,
            },
        },
        {
            key: 'relatieEcgEnKlachten',
            value: {
                label: translate.diagnosticReport.relationEcgAndComplaints,
                options: relationEcgAndComplaintsOptions(),
                type: AddendumAddEditValueInputType.typeAhead,
                value: customControlData?.relatieEcgEnKlachten,
            },
        },
        {
            key: 'overigeOpmerkingen',
            value: {
                label: translate.diagnosticReport.additionalRemarks,
                maxLength: 10000,
                type: AddendumAddEditValueInputType.textArea,
                value: customControlData?.overigeOpmerkingen,
            },
        },
    ]);

    const dateSent = diagnosticReport.dateSent ? DateTime.from(diagnosticReport.dateSent) : undefined;

    const [allowAddAddendum, setAllowAddAddendum] = useState(addAddendumTimeCheck(dateSent));

    useInterval(
        () => {
            setAllowAddAddendum(addAddendumTimeCheck(dateSent));
        },
        allowAddAddendum ? 1000 : null
    );

    return (
        <AddendumAddEdit
            disableCancel={disableInput}
            disableInput={disableInput || !allowAddAddendum}
            disableSend={disableInput || disableSend || !allowAddAddendum}
            onCancel={onCancel}
            onChange={handleOnChange}
            onSave={handleOnSave}
            value={addendum}
        />
    );

    function handleOnChange(value: IAddendumAddEditValue[]) {
        const changedValues = value.filter(
            ({ key: entryKey, value: entryValue }) => customControlData[entryKey] !== entryValue.value
        );

        setDisableSend(changedValues.length < 1);
    }

    async function handleOnSave(value: IAddendumAddEditValue[]) {
        setDisableInput(true);
        setAddendum(value);

        if (Object.keys(addendum).length > 0) {
            await createAddendum({
                id,
                processEntity: ProcessEntity.diagnosticReport,
                properties: getAddendumValue(value),
                serviceCode: ServiceCode.TCOH,
                signal: abortSignal,
            });
        }

        onSaved();
    }

    function getAddendumValue(value: IAddendumAddEditValue[]) {
        return value.reduce((previous, { key: entryKey, value: entryValue }) => {
            if (entryValue.checked && entryValue.value !== customControlData[entryKey]) {
                return {
                    ...previous,
                    [entryKey]: entryValue.value,
                };
            }

            return previous;
        }, {});
    }
}
